import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('nwc_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Redirect to login on 401
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('nwc_token');
      localStorage.removeItem('nwc_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// Users
export const getUsers = () => api.get('/users').then((r) => r.data);
export const createUser = (data: { name: string; email: string; password: string; role?: 'admin' | 'client'; projectIds?: string[] }) =>
  api.post('/users', data).then((r) => r.data);
export const updateUser = (id: string, data: { name?: string; password?: string; role?: 'admin' | 'client'; projectIds?: string[] }) =>
  api.patch(`/users/${id}`, data).then((r) => r.data);
export const deleteUser = (id: string) => api.delete(`/users/${id}`).then((r) => r.data);

// Auth
export const login = (email: string, password: string) =>
  api.post('/auth/login', { email, password }).then((r) => r.data);

export const getMe = () => api.get('/auth/me').then((r) => r.data);

// Clients
export const getClients = () =>
  api.get('/clients').then((r) =>
    [...r.data].sort((a: any, b: any) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
  );
export const createClient = (data: { name: string; color: string; notes?: string }) =>
  api.post('/clients', data).then((r) => r.data);
export const updateClient = (id: string, data: { name: string; color: string; notes?: string }) =>
  api.put(`/clients/${id}`, data).then((r) => r.data);
export const deleteClient = (id: string) =>
  api.delete(`/clients/${id}`).then((r) => r.data);

// Projects
export const getProjects = (clientId?: string) =>
  api.get('/projects', { params: clientId ? { clientId } : undefined }).then((r) => r.data);
export const createProject = (data: { name: string; clientId: string; status?: string; notes?: string }) =>
  api.post('/projects', data).then((r) => r.data);
export const updateProject = (id: string, data: { name?: string; clientId?: string; status?: string; notes?: string }) =>
  api.put(`/projects/${id}`, data).then((r) => r.data);
export const deleteProject = (id: string) =>
  api.delete(`/projects/${id}`).then((r) => r.data);

// Columns
export const getColumns = () => api.get('/columns').then((r) => r.data);
export const createColumn = (data: { name: string; color?: string }) =>
  api.post('/columns', data).then((r) => r.data);
export const updateColumn = (id: string, data: { name?: string; color?: string; position?: number }) =>
  api.put(`/columns/${id}`, data).then((r) => r.data);
export const deleteColumn = (id: string) =>
  api.delete(`/columns/${id}`).then((r) => r.data);

// Cards
export const getCards = (params?: { projectId?: string; columnId?: string; clientId?: string }) =>
  api.get('/cards', { params }).then((r) => r.data);
export const createCard = (data: {
  title: string;
  description?: string;
  projectId?: string;
  columnId: string;
  assigneeId?: string;
  priority?: string;
  dueDate?: string;
}) => api.post('/cards', data).then((r) => r.data);
export const updateCard = (id: string, data: Partial<{
  title: string;
  description: string;
  projectId: string;
  columnId: string;
  assigneeId: string;
  priority: string;
  dueDate: string;
}>) => api.put(`/cards/${id}`, data).then((r) => r.data);
export const deleteCard = (id: string) =>
  api.delete(`/cards/${id}`).then((r) => r.data);
export const moveCard = (id: string, columnId: string, position: number) =>
  api.patch(`/cards/${id}/move`, { columnId, position }).then((r) => r.data);
export const exportCards = async (columnId: string, startDate: string, endDate: string): Promise<Blob> => {
  const res = await api.get(`/cards/export`, {
    params: { columnId, startDate, endDate },
    responseType: 'blob',
  });
  return res.data;
};

// SubTasks
export const getSubTasks = (cardId: string) =>
  api.get(`/cards/${cardId}/subtasks`).then((r) => r.data);
export const createSubTask = (cardId: string, data: { label: string; dueDate?: string }) =>
  api.post(`/cards/${cardId}/subtasks`, data).then((r) => r.data);
export const updateSubTask = (cardId: string, sid: string, data: { label?: string; done?: boolean; dueDate?: string }) =>
  api.patch(`/cards/${cardId}/subtasks/${sid}`, data).then((r) => r.data);
export const deleteSubTask = (cardId: string, sid: string) =>
  api.delete(`/cards/${cardId}/subtasks/${sid}`).then((r) => r.data);

// Comments
export const getComments = (cardId: string) =>
  api.get(`/cards/${cardId}/comments`).then((r) => r.data);
export const createComment = (cardId: string, data: { body: string; author?: string }) =>
  api.post(`/cards/${cardId}/comments`, data).then((r) => r.data);

// Notes (freeform key-value notepad)
export const getNote = (key: string) =>
  api.get(`/notes/${encodeURIComponent(key)}`).then((r) => r.data);
export const saveNote = (key: string, content: string) =>
  api.put(`/notes/${encodeURIComponent(key)}`, { content }).then((r) => r.data);
